import { type ChangeEvent, type FormEvent } from 'react'
import { snapshot } from 'valtio'
import { RECURRING_FREQUENCY_SELECT_OPTIONS } from '../../../../../../shared/types/RecurringFrequencyType'
import { addToArray } from '../../../../../../shared/utils/arrayUtils'
import { recurringPaymentProxy } from '../../../../../../shared/proxies/recurringPaymentProxy'
import { executeRecurringConfigurationUpdate } from '../../RecurringPaymentDetail/executeRecurringConfigurationUpdate'
import { GtagEvents, gtmTrackButtonPress } from '../../../../../../shared/utils/gtmUtils'
import { cardAddProxy } from '../../../../../../shared/components/CardAddSlideOver/cardAddProxy'
import { recurringPaymentEditProxy, resetRecurringPaymentEditProxy } from './recurringPaymentEditProxy'
import {
  RecurringPaymentEditProxyFormFieldNamesEnum
} from './recurringPaymentEditProxyFormFieldNamesEnum'
import { normalizers } from './normalizers'
import { validators } from './validators'

export const buildFrequencyOption = (billingCycle: string): { label: string, value: string } | null => {
  const option = RECURRING_FREQUENCY_SELECT_OPTIONS.find((opt) => opt.value === billingCycle)
  return option ? { label: option.label, value: option.value } : null
}

export const handleFrequencyChange = (selectedOption: { label: string, value: string } | null): void => {
  if (selectedOption) {
    recurringPaymentEditProxy.form.values.billingCycle = selectedOption?.value
    recurringPaymentEditProxy.form.dirty = addToArray(recurringPaymentEditProxy.form.dirty, RecurringPaymentEditProxyFormFieldNamesEnum.BillingCycle)
  }
}

export function populateRecurringInformationForEdit (): void {
  const recurringPayment = recurringPaymentProxy.recurringPayment

  if (recurringPayment) {
    recurringPaymentEditProxy.customer = recurringPayment.customer || null
    recurringPaymentEditProxy.customerCard = recurringPayment.customerCard || null

    if (recurringPaymentEditProxy.customerCard) {
      recurringPaymentEditProxy.form.dirty = addToArray(recurringPaymentEditProxy.form.dirty, RecurringPaymentEditProxyFormFieldNamesEnum.CustomerCard)
    }

    recurringPaymentEditProxy.form.values.amount = recurringPayment.amountCents.toFixed(2)
    recurringPaymentEditProxy.form.dirty = addToArray(recurringPaymentEditProxy.form.dirty, RecurringPaymentEditProxyFormFieldNamesEnum.Amount)

    recurringPaymentEditProxy.form.values.invoiceNumber = recurringPayment.invoiceNumber
    recurringPaymentEditProxy.form.dirty = addToArray(recurringPaymentEditProxy.form.dirty, RecurringPaymentEditProxyFormFieldNamesEnum.InvoiceNumber)

    recurringPaymentEditProxy.form.values.notes = recurringPayment.notes
    recurringPaymentEditProxy.form.dirty = addToArray(recurringPaymentEditProxy.form.dirty, RecurringPaymentEditProxyFormFieldNamesEnum.Notes)

    recurringPaymentEditProxy.form.values.addressOne = recurringPayment.addressOne
    recurringPaymentEditProxy.form.dirty = addToArray(recurringPaymentEditProxy.form.dirty, RecurringPaymentEditProxyFormFieldNamesEnum.AddressOne)

    if (recurringPayment.addressTwo) {
      recurringPaymentEditProxy.form.values.addressTwo = recurringPayment.addressTwo
      recurringPaymentEditProxy.form.dirty = addToArray(recurringPaymentEditProxy.form.dirty, RecurringPaymentEditProxyFormFieldNamesEnum.AddressTwo)
    }

    recurringPaymentEditProxy.form.values.email = recurringPayment.email
    recurringPaymentEditProxy.form.dirty = addToArray(recurringPaymentEditProxy.form.dirty, RecurringPaymentEditProxyFormFieldNamesEnum.Email)

    recurringPaymentEditProxy.form.values.city = recurringPayment.city
    recurringPaymentEditProxy.form.dirty = addToArray(recurringPaymentEditProxy.form.dirty, RecurringPaymentEditProxyFormFieldNamesEnum.City)

    recurringPaymentEditProxy.form.values.state = recurringPayment.state
    recurringPaymentEditProxy.form.dirty = addToArray(recurringPaymentEditProxy.form.dirty, RecurringPaymentEditProxyFormFieldNamesEnum.State)

    recurringPaymentEditProxy.form.values.postalCode = recurringPayment.postalCode
    recurringPaymentEditProxy.form.dirty = addToArray(recurringPaymentEditProxy.form.dirty, RecurringPaymentEditProxyFormFieldNamesEnum.PostalCode)

    recurringPaymentEditProxy.form.values.countryCode = recurringPayment.countryCode
    recurringPaymentEditProxy.form.dirty = addToArray(recurringPaymentEditProxy.form.dirty, RecurringPaymentEditProxyFormFieldNamesEnum.CountryCode)

    recurringPaymentEditProxy.form.values.startDate = recurringPayment.startPaymentDate
    recurringPaymentEditProxy.form.dirty = addToArray(recurringPaymentEditProxy.form.dirty, RecurringPaymentEditProxyFormFieldNamesEnum.StartDate)

    if (recurringPayment.endDate) {
      recurringPaymentEditProxy.form.values.endDate = recurringPayment.endDate
      recurringPaymentEditProxy.form.dirty = addToArray(recurringPaymentEditProxy.form.dirty, RecurringPaymentEditProxyFormFieldNamesEnum.EndDate)
    }

    recurringPaymentEditProxy.form.values.billingCycle = recurringPayment.billingCycle
    recurringPaymentEditProxy.form.dirty = addToArray(recurringPaymentEditProxy.form.dirty, RecurringPaymentEditProxyFormFieldNamesEnum.BillingCycle)

    recurringPaymentEditProxy.stopOnError = recurringPayment.stopOnError
  }
}

export const handleChange = (fieldName: RecurringPaymentEditProxyFormFieldNamesEnum) => (e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>) => {
  // @ts-expect-error
  const val: string = e.target.type === 'checkbox' ? e.target.checked : e.target.value
  const normalizer = normalizers[fieldName]
  const normalized = normalizer ? normalizer(val) : val
  recurringPaymentEditProxy.form.values[fieldName] = normalized
  recurringPaymentEditProxy.form.dirty = addToArray(recurringPaymentEditProxy.form.dirty, fieldName)
  const validator = validators[fieldName]
  if (validator) { recurringPaymentEditProxy.form.errors[fieldName] = validator(normalized) }
}

export const handleBlur = (fieldName: RecurringPaymentEditProxyFormFieldNamesEnum) => () => {
  recurringPaymentEditProxy.form.blurred = addToArray(recurringPaymentEditProxy.form.blurred, fieldName)
}

export const handleSubmitForRecurringConfigurationUpdate = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
  const { displayConvergeSlideOver, displaySlideOverCAP } = snapshot(cardAddProxy)
  if (displayConvergeSlideOver || displaySlideOverCAP) {
    return
  }
  e.preventDefault()
  gtmTrackButtonPress(GtagEvents.CARD_MODAL_SUBMIT)

  const result = await executeRecurringConfigurationUpdate()
  const { apiCallEditConfiguration } = snapshot(recurringPaymentEditProxy)
  if (apiCallEditConfiguration.error ?? !result?.id) { return }

  recurringPaymentEditProxy.displaySlideOver = false
  resetRecurringPaymentEditProxy()
}
